/**
 * What the examiner says, matched back to the words as written.
 *
 * The runtime speaks and stores lines as plain text (backend
 * services/interview_runtime/text.py, plain()): markup gone, maths left as
 * $...$. On screen the authored markup is better: formulas, sub- and
 * superscripts and emphasis survive, and MathJax typesets the maths. So a
 * line the examiner said is matched back to the opening, follow-up or
 * follow-up branch it came from, by flattening each the same way the server
 * does and finding which ones the line ends with. Anything left over (a
 * lead-in such as "With that in mind...") stays as text.
 *
 * plainText() must agree with the server's plain() exactly: tests run both
 * over __fixtures__/plainCases.json.
 */
const TAGS = 'p|br|em|strong|b|i|u|s|sub|sup|span|a|ul|ol|li|div|table|thead|tbody|tr|td|th|'
  + 'img|h[1-6]|blockquote|code|pre|hr|small|mark|figure|figcaption|caption';
const ATTR = '(?:\\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*\\s*=\\s*(?:"[^"]*"|\'[^\']*\'|[^\\s"\'<>=`]+))';
const TAG = new RegExp(`</?(?:${TAGS})\\b${ATTR}*\\s*/?>`, 'gi');
const INLINE = new RegExp(`</?(?:em|strong|b|i|u|s|span|a|small|mark)\\b${ATTR}*\\s*/?>`, 'gi');
const SUB = new RegExp(`<sub\\b${ATTR}*\\s*>([\\s\\S]*?)</sub>`, 'gi');
const SUP = new RegExp(`<sup\\b${ATTR}*\\s*>([\\s\\S]*?)</sup>`, 'gi');
const MATH = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\])/;

/** Authored HTML with every "<" that does not start a real tag escaped, so a
 * browser parsing it keeps "$0<k<1$" as text rather than reading "<k<1$ ...>"
 * as a tag and dropping it. */
export function escapeStrayLessThan(html) {
  const s = String(html || '');
  const tag = new RegExp(`</?(?:${TAGS})\\b${ATTR}*\\s*/?>|<!--[\\s\\S]*?-->`, 'gi');
  let out = '';
  let last = 0;
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = tag.exec(s)) !== null) {
    out += s.slice(last, m.index).replace(/</g, '&lt;') + m[0];
    last = m.index + m[0].length;
  }
  return out + s.slice(last).replace(/</g, '&lt;');
}

function decodeEntities(s) {
  if (!s.includes('&')) return s;
  // A textarea decodes entities without parsing anything as a tag.
  const el = document.createElement('textarea');
  el.innerHTML = s;
  return el.value;
}

function stripChunk(chunk) {
  const out = chunk
    .replace(SUB, (_, inner) => inner.trim())
    .replace(SUP, (_, inner) => {
      const t = inner.trim();
      return /^[-+]?\w{1,3}$/.test(t) ? `^${t}` : `^(${t})`;
    })
    .replace(INLINE, '')
    .replace(TAG, ' ');
  return decodeEntities(out);
}

export function plainText(html) {
  const parts = String(html || '').split(MATH);
  return parts.map((p, i) => (i % 2 ? p : stripChunk(p))).join('').replace(/\s+/g, ' ').trim();
}

const words = (s) => (String(s || '').toLowerCase().match(/[a-z0-9]+/g) || []).join(' ');

/** Whether a question's opening is said again, word for word, by its first
 * follow-up: then it is said and shown once (backend interview.py,
 * opening_adds_nothing, is the same rule for what the examiner says). Some
 * first follow-ups restate the whole opening. */
export function openingAddsNothing(opening, firstFollowUp) {
  const a = words(plainText(opening));
  const b = words(plainText(firstFollowUp));
  return Boolean(a) && Boolean(b) && ` ${b} `.includes(` ${a} `);
}

/** [{html}|{text}] for one line the examiner said, in order. */
export function displayParts(question, said) {
  let rest = String(said || '').trim();
  if (!question || !rest) return rest ? [{ text: rest }] : [];
  const candidates = [];
  const add = (html) => {
    const p = plainText(html);
    if (p) candidates.push({ html, plain: p });
  };
  add(question.opening_prompt);
  (question.rungs || []).forEach((r) => {
    add(r.prompt);
    (r.branches || []).forEach((b) => add(b.prompt));
  });
  candidates.sort((a, b) => b.plain.length - a.plain.length);

  const endingOf = (line) => candidates.find((c) => line.endsWith(c.plain));
  const parts = [];
  for (let guard = 0; rest && guard < 8; guard += 1) {
    const hit = endingOf(rest);
    if (!hit) break;
    parts.unshift({ html: hit.html });
    rest = rest.slice(0, rest.length - hit.plain.length).trim();
  }
  if (rest) parts.unshift({ text: rest });
  return parts;
}
